import React, { useState } from 'react';
import { Button, Modal, InputNumber, Checkbox, Tag, Row, Col, Select } from 'antd';
import {
  ItemWithName,
  ProductAvailabilityResponse
} from '../../../../../core-module/services/deliverer/types';

interface Props {
  products: ItemWithName[];
  stock: ProductAvailabilityResponse;
  onOrderProducts: (items: Record<string, number>) => Promise<void>;
}

const OrderEditAddProductsFormComponent: React.FC<Props> = ({
  products,
  stock,
  onOrderProducts
}) => {
  const [checkedItems, setCheckedItems] = useState<Record<number, boolean>>({});
  const [toAddProducts, setToAddProducts] = useState<Record<string, number>>({});
  const [isOpen, setIsOpen] = useState(false);
  const [isOrderModalVisible, setIsOrderModalVisible] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [defaultPortionSize, setDefaultPortionSize] = useState('Medium');

  const itemsWithoutStock = new Set(
    Object.entries(stock.availableQuantities)
      .filter(([_, quantity]) => quantity <= 0)
      .map(([productId, _]) => productId)
  );

  const handleCheckboxChange = (item: ItemWithName, checked: boolean, index: number) => {
    setCheckedItems((prev) => ({
      ...prev,
      [index]: checked
    }));

    setToAddProducts((prev) => {
      const updated = { ...prev };

      if (checked) {
        // When checking, pre-fill the portion matching defaultPortionSize with quantity 1
        const matchingPortion = item.portions.find((p) => p.portionSize === defaultPortionSize);
        if (matchingPortion) {
          updated[matchingPortion.productId] = 1;
        }
      } else {
        // When unchecking, remove all portions of this product
        item.portions.forEach((p) => {
          delete updated[p.productId];
        });
      }

      return updated;
    });
  };

  const handleChange = (productId: string, value: number) => {
    setToAddProducts((prev) => {
      const updated = { ...prev };

      // Remove key if value is not valid anymore
      if (!value || value === 0) {
        delete updated[productId];
      }
      // Update value to new value
      else {
        updated[productId] = value;
      }

      return updated;
    });
  };

  function clearToAddProducts() {
    setToAddProducts({});
    setCheckedItems({});
  }

  const handleOrderProducts = async () => {
    if (Object.keys(toAddProducts).length === 0) return;
    setIsSaving(true);
    await onOrderProducts(toAddProducts);
    clearToAddProducts();

    setIsOpen(false);
    setIsOrderModalVisible(false);
    setIsSaving(false);
  };

  return (
    <>
      <div style={{ marginTop: '2em' }}>
        {!isOpen && (
          <Button type='primary' style={{ width: '100%' }} onClick={() => setIsOpen(true)}>
            Extra maaltijden toevoegen
          </Button>
        )}
      </div>

      {isOpen && (
        <>
          <Button
            type='primary'
            style={{ width: '100%', marginTop: 10, marginBottom: 10 }}
            disabled={Object.keys(toAddProducts).length === 0}
            onClick={() => setIsOrderModalVisible(true)}
          >
            {Object.keys(toAddProducts).length === 0
              ? 'Selecteer producten om te bestellen'
              : 'Bestel geselecteerde producten'}
          </Button>
          <Row>
            <div style={{ float: 'right', marginRight: 10 }}>
              <label>Pas een standaardportiegrootte toe: </label>
              <Select
                style={{ width: 220 }}
                placeholder={'- Maak een keuze -'}
                onChange={setDefaultPortionSize}
                value={defaultPortionSize || undefined}
              >
                <Select.Option value={'Small'}>Small (350g)</Select.Option>
                <Select.Option value={'Medium'}>Medium (450g)</Select.Option>
                <Select.Option value={'Large'}>Large (600g)</Select.Option>
              </Select>
            </div>
          </Row>

          {products.map((item, index) => (
            <Row gutter={5} key={index} style={{ marginTop: '20px', marginBottom: '20px' }}>
              <Col span={18}>
                <Checkbox
                  checked={checkedItems[index] || false}
                  onChange={(e) => handleCheckboxChange(item, e.target.checked, index)}
                  disabled={item.portions.every((portion) =>
                    itemsWithoutStock.has(portion.productId)
                  )}
                >
                  <Tag color='#ff9800' style={{ width: '32px', textAlign: 'center' }}>
                    {item.mealSequenceNumber}
                  </Tag>
                  {item.portions.every((portion) => itemsWithoutStock.has(portion.productId)) && (
                    <Tag color='red' style={{ marginBottom: '10px' }}>
                      Geen voorraad
                    </Tag>
                  )}
                  {item.mainVariantProductId} - {item.name}
                </Checkbox>
              </Col>

              {checkedItems[index] && (
                <Col span={6}>
                  {item.portions.map((portion, i) => (
                    <Row key={i} justify='space-between'>
                      <Col span={12} style={{ textAlign: 'right' }}>
                        <p style={{ lineHeight: '30px', paddingRight: '15px' }}>
                          {portion.portionSize}
                        </p>
                      </Col>
                      <Col span={12}>
                        {stock && stock.availableQuantities[portion.productId] > 0 ? (
                          <InputNumber
                            min={0}
                            value={toAddProducts[portion.productId] ?? 0}
                            onChange={(value) => handleChange(portion.productId, value ?? 0)}
                          />
                        ) : (
                          <p style={{ color: 'red', lineHeight: '30px' }}>Geen voorraad</p>
                        )}
                      </Col>
                    </Row>
                  ))}
                </Col>
              )}
            </Row>
          ))}

          <Modal
            title={`Producten toevoegen`}
            visible={isOrderModalVisible}
            onOk={handleOrderProducts}
            onCancel={() => setIsOrderModalVisible(false)}
            okText='Bestellen'
            cancelText='Annuleren'
            confirmLoading={isSaving}
          >
            <p>Weet je zeker dat je deze producten wilt bestellen?</p>
            {products
              .flatMap((product) =>
                product.portions
                  .filter((portion) => toAddProducts[portion.productId] > 0)
                  .map((portion) => ({
                    productName: product.name,
                    portionSize: portion.portionSize,
                    productId: portion.productId,
                    quantity: toAddProducts[portion.productId]
                  }))
              )
              .map((item) => (
                <div key={item.productId}>
                  {item.quantity}x {item.productName} - {item.portionSize}
                </div>
              ))}
          </Modal>
        </>
      )}
    </>
  );
};

export { OrderEditAddProductsFormComponent };
